import {
    ApiBaseService,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type {
    Automation,
    AutomationRunStats,
    FieldDefinition,
    Process,
} from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import StepService from "~/services/StepService";
import type { AutomationAppBase } from "~/models/Connection";
import AutomationProcessService from "~/services/AutomationProcessService";
import AutomationEventService from "~/services/AutomationEventService";
import type { AutomationConfig } from "~/stores/automation-store";
import type { StoreTemplate, Template } from "~/models/Template";
import type { UnwrapRef } from "vue";
import type { FetchOptions } from "ofetch";
import type { FilterGroup } from "~/components/automation/step/Filters.vue";
import {
    faArrowsSpin,
    faBolt,
    faClone,
    faPause,
    faPencil,
    faPlay,
    faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { faArrowRotateBack } from "@fortawesome/pro-light-svg-icons";
import DataLookupService from "./DataLookupService";

export default class AutomationService extends ApiBaseService {
    public steps(uuid: string): StepService {
        return new StepService(uuid, this.getConfig());
    }

    public processes(uuid: string): AutomationProcessService {
        return new AutomationProcessService(uuid, this.getConfig());
    }

    public events(uuid: string): AutomationEventService {
        return new AutomationEventService(uuid, this.getConfig());
    }

    public dataLookup(uuid: string): DataLookupService {
        return new DataLookupService(uuid, this.getConfig());
    }

    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Automation> {
        return this.paginate<Automation>(
            this.listRoute(),
            options as PaginatedFetchOptions,
        );
    }

    public listRoute(): string {
        return this.route("api.automations.index", "/automations");
    }

    public listRequest(
        options?: FetchOptions,
    ): Promise<ApiResponse<Automation[]>> {
        return this.request(this.listRoute(), options);
    }

    public create(
        data?: Partial<Automation>,
    ): Promise<ApiResponse<Automation>> {
        return this.request(
            this.route("api.automations.store", "/automations"),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public duplicate(uuid: string): Promise<ApiResponse<Automation>> {
        return this.request(
            this.route(
                "api.automations.duplicate",
                "/automations/{uuid}/duplicate",
                { uuid },
            ),
            {
                method: "POST",
                body: [],
            },
        );
    }

    public duplicateStep(
        uuid: string,
        StepCopiedUuid: string,
        StepAfterUuid: string,
    ): Promise<ApiResponse<Automation>> {
        return this.request(
            this.route(
                "api.automations.duplicate.step",
                "/automations/{uuid}/steps/duplicate",
                { uuid },
            ),
            {
                method: "POST",
                body: {
                    stepToBeCopied: StepCopiedUuid,
                    stepAfter: StepAfterUuid,
                },
            },
        );
    }

    public find(
        uuid: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<Automation>> {
        return this.request<ApiResponse<Automation>>(
            this.route("api.automations.show", "/automations/{uuid}", {
                uuid,
            }),
            options,
        );
    }

    public update(
        uuid: string,
        data: Partial<Automation>,
    ): Promise<ApiResponse<Automation>> {
        return this.request(
            this.route("api.automations.store", "/automations/{uuid}", {
                uuid,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public delete(uuid: string): Promise<null> {
        return this.request(
            this.route("api.automations.destroy", "/automations/{uuid}", {
                uuid,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public restore(uuid: string): Promise<null> {
        return this.request(
            this.route(
                "api.automations.restore",
                "/automations/{uuid}/restore",
                {
                    uuid,
                },
            ),
            {
                method: "PATCH",
            },
        );
    }

    public configuration(uuid: string): Promise<ApiResponse<AutomationConfig>> {
        return this.request(
            this.route("api.automations.config", "/automations/{uuid}/config", {
                uuid,
            }),
            {
                method: "GET",
            },
        );
    }

    public availableLiveStats(
        uuid: string,
    ): Promise<ApiResponse<{ available: string[] }>> {
        return this.request(
            this.route(
                "api.automations.config",
                "/automations/{uuid}/live-stats/available",
                { uuid },
            ),
            {
                method: "GET",
            },
        );
    }

    public getLiveStat<T>(uuid: string, key: string): Promise<ApiResponse<T>> {
        return this.request(
            this.route(
                "api.automations.config",
                "/automations/{uuid}/live-stats/{key}",
                {
                    uuid,
                    key,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public getRunStats(
        uuid: string,
        type: "process" | "event" | "task" | "none" = "none",
        id?: number,
    ): Promise<ApiResponse<AutomationRunStats>> {
        return this.request(
            this.route(
                "api.automations.run-stat",
                "/automations/{uuid}/run-stats" +
                    (type != "none" ? "/" + type + "/{id}" : ""),
                {
                    uuid,
                    id,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public baseConfiguration(): Promise<ApiResponse<AutomationConfig>> {
        return this.request(
            this.route("api.automations.base-config", "/automations/config"),
            {
                method: "GET",
            },
        );
    }

    public fields(
        uuid: string,
        automation_type: string | undefined = "any",
        process_id?: number,
    ): Promise<
        ApiResponse<{
            apps: Record<string, AutomationAppBase>;
            fields: FieldDefinition[];
        }>
    > {
        return this.request(
            this.route("api.automations.fields", "/automations/{uuid}/fields", {
                uuid,
            }),
            {
                method: "GET",
                query: {
                    automation_type,
                    process_id,
                },
            },
        );
    }

    public searchField(
        uuid: string,
        key: string,
        data?: Partial<{
            pagination: "none";
            search: string;
            operator: string;
            label_key: string;
            value_key: string;
            filters: FilterGroup;
            page: number;
            limit: number;
        }>,
    ): Promise<ApiResponse<Automation>> {
        return this.request(
            this.route(
                "api.automations.fields.search",
                "/automations/{uuid}/fields/{key}",
                {
                    uuid,
                    key,
                },
            ),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public searchObject(
        uuid: string,
        data?: Partial<{
            pagination: "none";
            search: string;
            page: number;
            limit: number;
        }>,
    ): Promise<ApiResponse<{ label: string; value: string }[]>> {
        return this.request(
            this.route(
                "api.automations.objects.search",
                "/automations/{uuid}/objects/search",
                {
                    uuid,
                },
            ),
            {
                method: "GET",
                params: data,
            },
        );
    }

    public showTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.automations.templates.store",
                "/automations/{uuid}/template",
                {
                    uuid,
                },
            ),
        );
    }

    public storeTemplate(
        uuid: string,
        data: Partial<StoreTemplate>,
    ): Promise<ApiResponse<Template>> {
        if (!(data.banner instanceof File)) {
            delete data.banner;
        }

        return this.request(
            this.route(
                "api.automations.templates.store",
                "/automations/{uuid}/template",
                {
                    uuid,
                },
            ),
            {
                method: "POST",
                body: jsonToFormData(data, {
                    booleanAsNumbers: true,
                }),
            },
        );
    }

    public disableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.automations.templates.delete",
                "/automations/{uuid}/template",
                {
                    uuid,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    public enableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.automations.templates.restore",
                "/automations/{uuid}/template/restore",
                {
                    uuid,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public runTest(
        uuid: string,
        data?: Partial<{
            test_type: "live" | "sandbox";
            record_type: "event" | "external" | "dummy";
            record_id: string | null;
            to_email: string | null;
            to_phone: string | null;
        }>,
        aborter?: AbortController,
    ): Promise<ApiResponse<Process>> {
        return this.request(
            this.route("api.automations.test", "/automations/{uuid}/test", {
                uuid,
            }),
            {
                method: "POST",
                body: data,
                signal: aborter?.signal,
            },
        );
    }

    public cancelTest(
        uuid: string,
        id?: number,
        isEvent?: boolean,
    ): Promise<ApiResponse<Process>> {
        return this.request(
            this.route(
                "api.automations.cancel-test",
                isEvent
                    ? "/automations/{uuid}/test/{id}/event"
                    : "/automations/{uuid}/test/{id}",
                {
                    uuid,
                    id,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    public getActionConfig<T>(
        uuid: string,
        app: string,
        action: string,
        body: Record<string, any> | UnwrapRef<Record<string, any>>,
    ): Promise<ApiResponse<T>> {
        return this.request<ApiResponse<T>>(
            this.route(
                "api.automations.action-config",
                "/automations/{uuid}/{app}/{action}/config",
                {
                    uuid,
                    app,
                    action,
                },
            ),
            {
                method: "POST",
                body: body && body.value ? body.value : body,
            },
        );
    }

    public navigate(uuid: string, edit: boolean) {
        navigateTo(`/automations/${uuid}` + (edit ? "/edit" : ""));
    }

    public toggleStatus(
        automation: Automation,
    ): Promise<ApiResponse<Automation>> {
        const uuid = automation.uuid;
        return this.request(
            this.route("api.automations.show", "/automations/{uuid}", {
                uuid,
            }),
        ).then((data) => {
            var newAutomationStatus = data.data;
            newAutomationStatus.status = newAutomationStatus.is_active
                ? "paused"
                : "active";
            return this.update(newAutomationStatus.uuid, newAutomationStatus);
        });
    }

    public validForAction(automation: Automation) {
        return (
            automation.trigger.app != "N/A" && automation.trigger.type != "N/A"
        );
    }

    public duplicateAutomation(automation: Automation) {
        return this.duplicate(automation.uuid);
    }

    public dropdown(
        automation: Automation,
        options: {
            deletable?: Ref<Automation | undefined>;
            restorable?: Ref<Automation | undefined>;
            forDuplication?: Ref<Automation | undefined>;
            automationStatusUpdatable?: Ref<Automation | undefined>;
            isEditing?: boolean;
        } = {
            isEditing: false,
        },
    ) {
        const items = [];

        if (automation.deleted_at) {
            if (options.restorable) {
                items.push({
                    label: "Restore",
                    icon: faArrowRotateBack,
                    click: () =>
                        options.restorable &&
                        (options.restorable.value = automation),
                });
            }

            if (options.forDuplication) {
                items.push({
                    label: "Duplicate",
                    icon: faClone,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        options.forDuplication &&
                        (options.forDuplication.value = automation),
                });
            } else {
                items.push({
                    label: "Duplicate",
                    icon: faClone,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        this.duplicateAutomation(automation).then((data) => {
                            useToast().add({
                                title: "Automation duplicated",
                                description:
                                    "You can now edit the new automation",
                                color: "primary",
                            });
                            navigateTo(`/automations/${data.data.uuid}/edit`);
                        }),
                });
            }
        } else {
            if (automation.status == "paused" || automation.status == "draft") {
                items.push({
                    label: "Activate",
                    icon: faPlay,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        options.automationStatusUpdatable &&
                        (options.automationStatusUpdatable.value = automation),
                });
            } else if (automation.status !== "finished") {
                items.push({
                    label: "Pause",
                    icon: faPause,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        options.automationStatusUpdatable &&
                        (options.automationStatusUpdatable.value = automation),
                });
            }

            if (!options.isEditing && automation.status !== "finished") {
                items.push({
                    label: "Edit",
                    icon: faPencil,
                    to: `/automations/${automation.uuid}/edit`,
                });
            }

            if (options.forDuplication) {
                items.push({
                    label: "Duplicate",
                    icon: faClone,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        options.forDuplication &&
                        (options.forDuplication.value = automation),
                });
            } else {
                items.push({
                    label: "Duplicate",
                    icon: faClone,
                    disabled: !this.validForAction(automation),
                    click: () =>
                        this.duplicateAutomation(automation).then((data) => {
                            useToast().add({
                                title: "Automation duplicated",
                                description:
                                    "You can now edit the new automation",
                                color: "primary",
                            });
                            navigateTo(`/automations/${data.data.uuid}/edit`);
                        }),
                });
            }

            if (options.deletable) {
                items.push({
                    label: "Delete",
                    icon: faTrash,
                    click: () =>
                        options.deletable &&
                        (options.deletable.value = automation),
                });
            }
        }

        return [
            items,
            [
                {
                    label: "Related",
                    disabled: true,
                },
                {
                    icon: faArrowsSpin,
                    label: "Events",
                    to: {
                        name: "automations-id-events",
                        params: {
                            id: automation.uuid,
                        },
                    },
                },
                {
                    label: "Runs",
                    icon: faArrowsSpin,
                    to: {
                        name: "automations-id-runs",
                        params: {
                            id: automation.uuid,
                        },
                    },
                },
                {
                    label: "Tasks",
                    icon: faBolt,
                    to: {
                        name: "automations-id-tasks",
                        params: {
                            id: automation.uuid,
                        },
                    },
                },
            ],
        ];
    }

    public generateTemplateFieldsSuggestions(
        id: number | string,
        data: {
            title: string | undefined;
            description: string | undefined;
            tags: (string | undefined)[];
        },
    ): Promise<ApiResponse<Template & { error?: string }>> {
        return this.request(
            this.route(
                "api.automations.template.generate-fields-suggestions",
                "/automations/{id}/template/generate-fields-suggestions",
                { id },
            ),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public removeTemplateBanner(
        uuid: number | string,
    ): Promise<ApiResponse<void>> {
        return this.request(
            this.route(
                "api.automations.template.remove-banner",
                "/automations/{uuid}/template/remove-banner",
                { uuid },
            ),
            {
                method: "DELETE",
            },
        );
    }
}
